import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CircleHelp } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { generatePath, useNavigate } from 'react-router-dom';

import UserContext from 'context/userContext';
import { queryClient } from '@api/queryClient';
import { Label } from '@epoch-ui/components/Form/label';
import { TextareaField } from '@epoch-ui/components/Form/fields/TextareaField/TextareaField';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalFooter
} from '@epoch-ui/components/Modal/modal';
import { Button } from '@epoch-ui/components/Button';
import { LinkButton } from '@epoch-ui/components/LinkButton';
import { Form } from '@epoch-ui/components/Form';
import { CheckboxField } from '@epoch-ui/components/Form/fields/CheckboxField/CheckboxField';
import { SwitchField } from '@epoch-ui/components/Form/fields/SwitchField/SwitchField';
import { useToaster } from 'hooks/useToaster';
import { ToasterVariant } from 'context/toasterContext';
import { DASHBOARD_EVENT_EDITOR_ROUTE, EVENTS_LIST_ROUTE } from 'constants/routes';
import { CalendarDetails } from 'types/event.types';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { useCancelEventModal } from '@event-editor/hooks/eventEditorState';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { publishedEventApi, CancelEventInput } from '@event-editor/api/published-event';

import { cancelEventSchema, CancelEventFormValues } from './cancelEventSchema';

interface CancelEventModalContentProps {
  eventId?: number;
  title?: string;
  calendarDetails?: CalendarDetails;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const CancelEventModalContent: React.FC<CancelEventModalContentProps> = ({
  eventId,
  title,
  calendarDetails,
  open,
  onOpenChange,
  onSuccess
}) => {
  const user = React.useContext(UserContext);
  const { showToast } = useToaster();
  const navigate = useNavigate();

  const usesOutlook = user?.companyUsesOutlookCalendar;
  const calendarExists = calendarDetails?.creatorFirstName && calendarDetails?.creatorLastName;
  const hasOutlookCalendarEvent = !!usesOutlook && !!calendarExists;

  const methods = useForm<CancelEventFormValues>({
    resolver: zodResolver(cancelEventSchema),
    defaultValues: {
      notifyGuests: hasOutlookCalendarEvent,
      emailNotification: hasOutlookCalendarEvent,
      slackNotification: false,
      slackMessage: null
    }
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    watch,
    setValue
  } = methods;

  const [notifyGuests, slackNotification, emailNotification] = watch([
    'notifyGuests',
    'slackNotification',
    'emailNotification'
  ]);

  let emailInputDescription: string | undefined;
  if (hasOutlookCalendarEvent) {
    emailInputDescription = 'Required for Outlook';
  } else if (!calendarExists) {
    emailInputDescription = 'No calendar event created for this event';
  }

  const close = (): void => onOpenChange(false);
  const openModal = (): void => onOpenChange(true);

  const handleNotifyGuestsChange = (checked: boolean): void => {
    setValue('notifyGuests', checked, { shouldDirty: true, shouldValidate: true });
    if (calendarExists) {
      setValue('emailNotification', checked, { shouldDirty: true, shouldValidate: true });
    }
    setValue('slackNotification', checked, { shouldDirty: true, shouldValidate: true });
    if (!checked) {
      setValue('slackMessage', null, { shouldDirty: true, shouldValidate: true });
    }
  };

  const cancelEventMutation = useMutation({
    mutationFn: (variables: CancelEventInput) => publishedEventApi.cancel(variables),
    onSuccess: (result: any, variables) => {
      queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(variables.eventId) });
      close();
      showToast(
        <span>
          <LinkButton
            variant="link"
            className="tw-h-5 tw-cursor-pointer tw-p-0 tw-font-normal tw-text-white"
            onClick={() =>
              navigate(
                generatePath(DASHBOARD_EVENT_EDITOR_ROUTE, { id: String(variables.eventId) })
              )
            }
          >
            {title || 'Event'}
          </LinkButton>{' '}
          was cancelled.
        </span>
      );
      if (onSuccess) {
        onSuccess();
      } else {
        navigate(`${EVENTS_LIST_ROUTE}?tab=archived`);
      }
    },
    onError: (error) => {
      console.error('Error cancelling event:', error);
      showToast('Failed to cancel event. Please try again.', {
        variant: ToasterVariant.ERROR
      });
    }
  });

  const onModalSubmit = (values: CancelEventFormValues): void => {
    if (!eventId) return;
    cancelEventMutation.mutate({
      eventId,
      ...values
    });
  };

  const isLoading = isSubmitting || cancelEventMutation.isPending;

  return (
    <Modal open={open} onOpenChange={(v) => (v ? openModal() : close())}>
      <ModalContent className="tw-max-w-[581px]" data-testid="cancel-event-modal">
        <ModalHeader className="tw-mb-4">
          <ModalTitle>Cancel event?</ModalTitle>
          <ModalDescription>
            Canceling this event will remove it from Epoch and {usesOutlook ? 'Outlook' : 'Google'}{' '}
            Calendar. You can restore the event within 30 days, but not the guest list.
          </ModalDescription>
        </ModalHeader>

        <Form {...methods}>
          <form
            onSubmit={handleSubmit(onModalSubmit, (errors) => {
              console.error(errors);
            })}
            className="tw-flex tw-flex-col"
          >
            {/* Notify guests toggle - now part of the form */}
            <SwitchField
              name="notifyGuests"
              label="Notify guests?"
              className="tw-mb-4"
              disabled={hasOutlookCalendarEvent}
              onCheckedChange={handleNotifyGuestsChange}
            />

            {/* Conditional form fields */}
            {notifyGuests && (
              <div className="tw-space-y-4">
                <div className="tw-space-y-2">
                  <CheckboxField
                    name="emailNotification"
                    label={`Email from ${usesOutlook ? 'Outlook' : 'Google'} Calendar ${
                      calendarExists
                        ? `(${calendarDetails?.creatorFirstName} ${calendarDetails?.creatorLastName})`
                        : ''
                    }`}
                    description={emailInputDescription}
                    disabled={!calendarExists || hasOutlookCalendarEvent}
                    onCheckedChange={(checked) => {
                      if (!checked && !slackNotification && notifyGuests) {
                        setValue('notifyGuests', false, {
                          shouldDirty: true,
                          shouldValidate: true
                        });
                      }
                    }}
                  />

                  <CheckboxField
                    name="slackNotification"
                    label="Slack"
                    onCheckedChange={(checked) => {
                      setValue('slackMessage', null, { shouldDirty: true, shouldValidate: true });
                      if (!emailNotification && !checked && notifyGuests) {
                        setValue('notifyGuests', false, {
                          shouldDirty: true,
                          shouldValidate: true
                        });
                      }
                    }}
                  />
                </div>

                {slackNotification && (
                  <div className="tw-space-y-4 tw-rounded-3xl tw-border-[1.5px] tw-border-solid tw-border-border-grey tw-p-5">
                    <div>
                      <Label htmlFor="slack-cancellation-message" className="tw-mb-1">
                        {title} was cancelled
                      </Label>
                      <p className="tw-text-xs tw-leading-4 tw-text-muted-foreground">
                        {emailNotification && (
                          <>
                            Sent as the subject for email notifications <br />
                          </>
                        )}
                        Sent as the message for Slack notifications
                      </p>
                    </div>

                    <TextareaField
                      id="slack-cancellation-message"
                      name="slackMessage"
                      label="Add optional message"
                      helpText="This will be included in the Slack message"
                      rows={4}
                      className="tw-w-full tw-resize-none"
                      inputClassName="tw-min-h-[132px]"
                      placeholder="Optional"
                    />
                  </div>
                )}
              </div>
            )}

            <ModalFooter className="tw-mt-5">
              <div className="tw-flex tw-w-full tw-items-center tw-justify-between">
                <a
                  href="https://help.epochapp.com/articles/canceled-events"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-flex tw-h-4 tw-items-center tw-p-0 tw-text-sm tw-text-primary-grey tw-no-underline hover:tw-underline"
                >
                  <CircleHelp className="tw-mr-2 tw-h-4 tw-w-4" />
                  Help
                </a>

                <div className="tw-flex tw-gap-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    type="button"
                    onClick={close}
                    disabled={isLoading}
                  >
                    Keep event
                  </Button>

                  <Button
                    variant="destructive"
                    size="sm"
                    type="submit"
                    disabled={isLoading}
                    loading={isLoading}
                    className="tw-rounded-xl"
                  >
                    Cancel event
                  </Button>
                </div>
              </div>
            </ModalFooter>
          </form>
        </Form>
      </ModalContent>
    </Modal>
  );
};

export const CancelEventModal: React.FC = () => {
  const { id: eventId, calendarDetails, title } = useEditableEvent();
  const { isOpen, close, open } = useCancelEventModal();
  return (
    <CancelEventModalContent
      eventId={eventId}
      title={title}
      calendarDetails={calendarDetails}
      open={isOpen}
      onOpenChange={(v) => (v ? open() : close())}
    />
  );
};
